import { GameIndicator, GameModel } from '@/models/GameModel';
import { GameProductSelectorAdvancedData, GameProductSelectorData } from './Data';
import useDevice from '@/hooks/useDevice';
import { TransitionTypes } from '@/typings/types/types';

interface GridState {
  columns: string;
  rows: string;
  space: string;
  center: string;
}

interface GameProductSelectorGeneralState {
  grid: GridState;
  loop: boolean;
  showDots: boolean;
  dotColor: string;
  activeState: string;
  bgColor: string;
  hoverColor: string;
  showAnswerButton: boolean;
  answerBtn: string;
  selectAnswerBtn: string;
  espEnabled: boolean;
}

interface GameProductSelectorImageState {
  id: string;
  image?: string;
  label?: string;
}

export interface GameProductSelectorAdvancedSoundState {
  enabled: boolean;
  src?: string;
}
export interface GameProductSelectorAdvancedAnimationState {
  enabled: boolean;
  animation?: TransitionTypes;
}
export interface GameProductSelectorAdvancedState {
  sound: GameProductSelectorAdvancedSoundState;
  animation: GameProductSelectorAdvancedAnimationState;
}

export interface GameProductSelectorState {
  general: GameProductSelectorGeneralState;
  images: GameProductSelectorImageState[];
  advanced: GameProductSelectorAdvancedState;
  isMobile: boolean;
  sliderOptions: {
    pagination: boolean;
    slidesPerView: number;
    slidesPerColumn: number;
    spaceBetween: number;
    centeredSlides: boolean;
    loop: boolean;
  };
  elementStyling: {
    generalStyling?: string;
  };
}

export class GameProductSelectorModel extends GameModel<GameProductSelectorData, GameProductSelectorState> {
  parseGame(data: GameProductSelectorData) {
    const state = this.state;

    const { isMobile } = useDevice();

    state.isMobile = isMobile;

    //Set default slider options
    state.sliderOptions = state.sliderOptions ?? {};
    state.sliderOptions.slidesPerView = 1;
    state.sliderOptions.slidesPerColumn = 1;
    state.sliderOptions.pagination = false;
    //End set default slider options

    state.general = state.general ?? {};
    state.general.activeState = data.general?.active_state;
    state.general.bgColor = data.general?.bg_color;
    if (data.general?.answer_btn) {
      state.general.answerBtn = data.general?.answer_btn;
    }
    state.general.espEnabled = data.general?.esp?.enabled === '1';
    if (data.general?.dot_color) {
      state.general.dotColor = data.general?.dot_color;
    }
    state.general.hoverColor = data.general?.hover_color;
    state.general.loop = data.general?.loop === '1';
    state.general.showAnswerButton = data.general?.show_answer_button === '1';
    if (data.general?.select_answer_btn) {
      state.general.selectAnswerBtn = data.general?.select_answer_btn;
    }
    state.general.showDots = data.general?.show_dots === '1';

    if (state.isMobile && data.general?.grid.overwrite_mobile !== '1') {
      if (data.general?.grid?.mobile) {
        state.general.grid = data.general?.grid?.mobile;
      }
    } else {
      state.general.grid = data.general?.grid?.desktop ?? {};
    }

    // Set images
    if (data.images.images) {
      state.images = data.images.images;
    }

    //Custom slider options
    if (state.general.grid && state.general.grid.columns) {
      state.sliderOptions.slidesPerColumn = parseInt(state.general.grid.rows);
    }

    if (state.general.grid && state.general.grid.rows) {
      state.sliderOptions.slidesPerView = parseInt(state.general.grid.columns);
    }

    if (state.general.showDots) {
      state.sliderOptions.pagination = true;
    }

    if ((state.general.grid.center && state.general.grid.center === 'center') || state.general.grid.center === '') {
      state.sliderOptions.centeredSlides = true;
    }
    if (!state.general.grid.center) {
      state.sliderOptions.centeredSlides = true;
    }

    if (state.general.grid.space && state.general.grid.space !== '') {
      state.sliderOptions.spaceBetween = parseInt(state.general.grid.space);
    }

    if (state.general.loop) {
      state.sliderOptions.loop = true;
    }

    if (state.images && state.general.grid) {
      if (state.images.length <= Number(state.general.grid.columns) * Number(state.general.grid.rows)) {
        state.sliderOptions.loop = false;
      }
    }
    //End custom slider options

    state.elementStyling = {};
    state.elementStyling.generalStyling = GameProductSelectorModel.constructGeneralStyle(state);

    state.advanced = state.advanced ?? {};
    state.advanced.sound = GameProductSelectorModel.constructAdvancedSoundState(data?.advanced);
    state.advanced.animation = GameProductSelectorModel.constructAdvancedAnimationState(data?.advanced);
  }

  private static constructAdvancedSoundState(
    data: GameProductSelectorAdvancedData | undefined
  ): GameProductSelectorAdvancedSoundState {
    return {
      enabled: !!data?.sound,
      ...(data?.sound && { src: data.sound })
    };
  }

  private static constructAdvancedAnimationState(
    data: GameProductSelectorAdvancedData | undefined
  ): GameProductSelectorAdvancedAnimationState {
    return {
      enabled: !!data?.animation,
      ...(data?.animation && { type: data.animation })
    };
  }

  private static constructGeneralStyle(state: GameProductSelectorState): string {
    let CSSString = '';

    if (state.general.dotColor && state.general.dotColor !== '') {
      CSSString += `.swiper-pagination-bullet-active {
                  background-color: ${state.general.dotColor};
                }`;
    }

    if (state.general.activeState !== 'alfa') {
      CSSString += `.product-selector .product-selector__image {
                    background-color: ${state.general.bgColor};
                  }`;

      CSSString += `
        .no-touch .product-selector .product-selector__image:hover,
                  .touch .product-selector .product-selector__image:active,
                  .product-selector .product-selector__image--selected {
                    background-color: ${state.general.hoverColor};
                  }';
      `;
    }

    return CSSString;
  }

  public getIndicators(): GameIndicator[] {
    return [];
  }

  public isGameValid(): boolean {
    // is valid logic here
    // const data = this.getData();
    return true;
  }

  public authorSignature() {
    return 'Nicky Christensen';
  }
}
